import { useApi } from '@/composables/useApi'

export type SubscriptionStatus =
  | 'pending_payment'
  | 'trialing'
  | 'active'
  | 'expired'
  | 'canceled'
  | 'free'
  | string

export type InvoiceStatus =
  | 'created'
  | 'sent'
  | 'paid'
  | 'canceled'
  | 'expired'
  | string

export type PaymentStatus = 'pending' | 'succeeded' | 'failed' | 'canceled' | string

export type InvoicePayment = {
  id: number
  invoice_id: number
  provider?: string | null
  external_id?: string | null
  amount: string | number
  currency: string
  status: PaymentStatus
  initiated_at?: string | null
  paid_at?: string | null
  failed_at?: string | null
}

export type SubscriptionItem = {
  id?: number
  capability_id?: number
  code?: string
  name?: string
  type?: 'feature' | 'limit' | string
  value?: number | null
  included_value?: number | null
  max_value?: number | null
  capability?: {
    id?: number
    code?: string
    name?: string
    type?: 'feature' | 'limit' | string
  }
}

export type SubscriptionPlan = {
  id?: number
  code?: string
  name?: string
  description?: string | null
  base_price?: number | null
}

export type SubscriptionInvoice = {
  id: number
  number?: string
  status?: InvoiceStatus
  amount?: number
  total?: number
  period?: string
  due_date?: string | null
  created_at?: string
  paid_at?: string | null
  email_to?: string | null
  email_status?: string | null
  last_error?: string | null
  description?: string | null
  seller_snapshot?: Record<string, unknown> | null
  customer_snapshot?: Record<string, unknown> | null
  payments?: InvoicePayment[]
  /** Есть сохранённый PDF в storage (ответ API после внедрения бэкенда) */
  has_pdf?: boolean
  pdf_path?: string | null
  pdf_generated_at?: string | null
  modulbank_operation_id?: string | null
  items?: Array<{
    name?: string
    qty?: number
    quantity?: number
    unit?: string
    vat?: string
    price?: number
    sum?: number
    total?: number
  }>
}

export type SubscriptionOrganization = {
  id: number
  client_id?: number
  name?: string
  email?: string
  role?: string
  organization?: {
    id?: number
    name?: string
    email?: string
  }
  pivot?: {
    role?: string
  }
}

export type CurrentSubscriptionResponse = {
  id?: number
  owner_id?: number
  status?: SubscriptionStatus
  started_at?: string | null
  ends_at?: string | null
  plan?: SubscriptionPlan | null
  items?: SubscriptionItem[]
  organizations?: SubscriptionOrganization[]
  organizations_count?: number
  last_invoice?: SubscriptionInvoice | null
}

export type PayInvoiceDto = Record<string, never>

export type BankTransferPayResponse = {
  mode: 'bank_transfer'
  invoice_id: number
  amount: string
  currency: string
  status: string
  payment_purpose: string
  payment_marker: string
  requisites?: Record<string, string>
  instructions?: string
  message?: string
}

export type PayInvoiceResponse = {
  success: boolean
  data: BankTransferPayResponse
}

export type InvoicesQuery = {
  per_page?: number
  page?: number
  sort?: string
  status?: string | string[]
}

export type CreateInvoiceDto = {
  plan_id: number
  period?: string
  email_to?: string
  capabilities?: {
    features?: number[]
    limits?: Record<string, number>
  }
}

const SUBSCRIPTION_BADGE_STORAGE_KEY = 'subscription_badge'

export type SubscriptionBadgeData = {
  plan: string
  periodStart: string
  periodEnd: string
  nextPaymentDate: string
  nextPaymentAmount: number
  isActive: boolean
  status: string
  lastInvoiceId: number | null
}

function parseDate(value?: string | null): string {
  if (!value) return ''
  const dt = new Date(value)
  if (Number.isNaN(dt.getTime())) return ''
  return dt.toISOString().slice(0, 10)
}

export type SubscriptionApiPayload = {
  subscription?: {
    plan?: { name?: string }
    status?: string
    started_at?: string | null
    created_at?: string | null
    ends_at?: string | null
  }
  last_invoice?: { id?: number; amount?: number; total?: number }
} | CurrentSubscriptionResponse | null

function normalizeToBadgeData(payload: SubscriptionApiPayload): SubscriptionBadgeData | null {
  if (!payload || typeof payload !== 'object') return null

  const sub = (payload as any).subscription ?? payload
  const lastInvoice = (payload as any).last_invoice ?? (payload as any).last_invoice

  const planName = sub?.plan?.name ?? ''
  const start = parseDate(sub?.started_at ?? sub?.created_at)
  const end = parseDate(sub?.ends_at)
  const status = sub?.status ?? ''
  const invoiceAmount = Number(lastInvoice?.amount ?? lastInvoice?.total ?? 0)
  const lastInvoiceId = lastInvoice?.id ?? null

  return {
    plan: planName,
    periodStart: start,
    periodEnd: end,
    nextPaymentDate: end,
    nextPaymentAmount: Number.isFinite(invoiceAmount) ? invoiceAmount : 0,
    isActive: status === 'active' || status === 'trialing',
    status,
    lastInvoiceId,
  }
}

export function saveSubscriptionBadgeToStorage(data: SubscriptionApiPayload) {
  if (!process.client) return
  const badge = normalizeToBadgeData(data)
  if (!badge) {
    localStorage.removeItem(SUBSCRIPTION_BADGE_STORAGE_KEY)
    return
  }
  localStorage.setItem(SUBSCRIPTION_BADGE_STORAGE_KEY, JSON.stringify(badge))
}

export function loadSubscriptionBadgeFromStorage(): SubscriptionBadgeData | null {
  if (!process.client) return null
  try {
    const raw = localStorage.getItem(SUBSCRIPTION_BADGE_STORAGE_KEY)
    if (!raw) return null
    return JSON.parse(raw) as SubscriptionBadgeData
  } catch {
    return null
  }
}

export async function fetchAndSaveSubscriptionForBadge(): Promise<void> {
  if (!process.client) return
  const config = useRuntimeConfig()
  const token = localStorage.getItem('access_token')
  if (!token) return

  const clientId = localStorage.getItem('current_client_id')
  const headers: Record<string, string> = {
    Accept: 'application/json',
    Authorization: `Bearer ${token}`,
  }
  if (clientId) headers['X-Client-Id'] = clientId

  const baseURL = config.public?.apiBaseUrl || 'http://127.0.0.1:8000'

  try {
    let payload: any = null
    const res = await $fetch<any>('/api/subscriptions/me', { baseURL, headers })
    payload = (res && typeof res === 'object' && 'data' in res) ? (res as any).data : res
    saveSubscriptionBadgeToStorage(payload)
  } catch {
    saveSubscriptionBadgeToStorage(null)
  }
}

export function getMySubscription() {
  return useApi<CurrentSubscriptionResponse | { data: CurrentSubscriptionResponse }>('/api/subscriptions/me', {
    method: 'GET',
  })
}

export function startTrial(planId: number) {
  return useApi('/api/subscriptions/trial', {
    method: 'POST',
    body: { plan_id: planId },
  })
}

export function cancelSubscriptionAtPeriodEnd(subscriptionId: number, reason?: string) {
  return useApi(`/api/subscriptions/${subscriptionId}/cancel`, {
    method: 'POST',
    body: reason ? { reason } : {},
  })
}

export function resumeSubscription(subscriptionId: number) {
  return useApi(`/api/subscriptions/${subscriptionId}/resume`, {
    method: 'POST',
  })
}

export function renewSubscription(subscriptionId: number, dto: { period?: string; email_to?: string }) {
  return useApi(`/api/subscriptions/${subscriptionId}/renew`, {
    method: 'POST',
    body: dto,
  })
}

export function getCurrentSubscriptionOrganizations(subscriptionId: number) {
  return useApi<SubscriptionOrganization[] | { data: SubscriptionOrganization[] }>(`/api/subscriptions/${subscriptionId}/organizations`, {
    method: 'GET',
  })
}

export function addCurrentSubscriptionOrganization(subscriptionId: number, clientId: number, role?: string) {
  return useApi(`/api/subscriptions/${subscriptionId}/organizations`, {
    method: 'POST',
    body: {
      client_id: clientId,
      ...(role ? { role } : {}),
    },
  })
}

export function removeCurrentSubscriptionOrganization(subscriptionId: number, organizationId: number) {
  return useApi(`/api/subscriptions/${subscriptionId}/organizations/${organizationId}`, {
    method: 'DELETE',
  })
}

export function getAvailableCapabilitiesForOrg(subscriptionId: number, orgId: number) {
  return useApi(`/api/subscriptions/${subscriptionId}/organizations/${orgId}/capabilities/available`, {
    method: 'GET',
  })
}

export type OrgCapabilitiesCheckoutDto = {
  capability_ids: number[]
  period_months: number
}

export function checkoutCapabilitiesForOrg(
  subscriptionId: number,
  orgId: number,
  dto: OrgCapabilitiesCheckoutDto,
) {
  return useApi(`/api/subscriptions/${subscriptionId}/organizations/${orgId}/capabilities/checkout`, {
    method: 'POST',
    body: dto,
  })
}

export function createInvoice(dto: CreateInvoiceDto) {
  return useApi<SubscriptionInvoice | { data: SubscriptionInvoice }>('/api/invoices', {
    method: 'POST',
    body: dto,
  })
}

export function getInvoices(params?: InvoicesQuery) {
  return useApi<SubscriptionInvoice[] | { data: SubscriptionInvoice[]; meta?: any }>('/api/invoices', {
    method: 'GET',
    params,
  })
}

export function getInvoice(invoiceId: number) {
  return useApi<SubscriptionInvoice | { data: SubscriptionInvoice }>(`/api/invoices/${invoiceId}`, {
    method: 'GET',
  })
}

/** Реквизиты и назначение платежа для банковского перевода (без эквайринга). */
export function payInvoice(invoiceId: number, dto: PayInvoiceDto = {}, idempotencyKey: string) {
  return useApi<PayInvoiceResponse>(`/api/invoices/${invoiceId}/pay`, {
    method: 'POST',
    body: dto,
    headers: {
      'Idempotency-Key': idempotencyKey,
    },
  })
}

/** Отмена неоплаченного счёта (POST /api/invoices/{id}/cancel). */
export function cancelInvoice(invoiceId: number) {
  return useApi<{ success?: boolean; data?: SubscriptionInvoice } | null>(`/api/invoices/${invoiceId}/cancel`, {
    method: 'POST',
  })
}

/** @deprecated используйте cancelInvoice */
export function deleteInvoice(invoiceId: number) {
  return cancelInvoice(invoiceId)
}

/**
 * Скачать PDF счёта (GET /api/invoices/{id}/pdf).
 */
export async function downloadInvoicePdf(invoiceId: number): Promise<Blob> {
  if (!process.client)
    throw new Error('downloadInvoicePdf: только на клиенте')

  const config = useRuntimeConfig()
  const baseURL = String(config.public?.apiBaseUrl || '/').replace(/\/$/, '')
  const token = localStorage.getItem('access_token')
  const clientId = localStorage.getItem('current_client_id')
  const headers: Record<string, string> = {
    Accept: 'application/pdf, application/octet-stream, */*',
  }
  if (token)
    headers.Authorization = `Bearer ${token}`
  if (clientId)
    headers['X-Client-Id'] = clientId

  const res = await fetch(`${baseURL}/api/invoices/${invoiceId}/pdf`, {
    method: 'GET',
    headers,
  })

  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`
    try {
      const ct = res.headers.get('content-type')
      const bodyText = ct?.includes('json') ? JSON.stringify(await res.json()) : await res.text()
      if (bodyText)
        detail = bodyText.slice(0, 500)
    } catch {
      /* ignore */
    }
    throw new Error(detail || `Ошибка загрузки PDF (${res.status})`)
  }

  return res.blob()
}
